import { HOST_TO_API_HOST } from '@/config/hosts'
import axios, {
  type AxiosRequestConfig,
  type AxiosResponse,
  AxiosError,
  type InternalAxiosRequestConfig
} from 'axios'
import { getActivePinia } from 'pinia'
import type { ApiError } from '@/types/auth'
import { reportClientError, isReporterEndpoint } from '@/services/errorReporter'

// Custom error class for session expiration
// This allows components to identify session-related errors and avoid showing duplicate messages
export class SessionExpiredError extends Error {
  constructor(message: string = 'Your session has expired. Please login to continue or refresh your page.') {
    super(message)
    this.name = 'SessionExpiredError'
  }
}

// Helper to check if an error is a session expiration error
export function isSessionExpiredError(error: any): error is SessionExpiredError {
  return error?.name === 'SessionExpiredError' || error instanceof SessionExpiredError
}

// Store reference to avoid circular dependency during initialization
let authStoreRef: (() => any) | null = null
let cachedAuthStore: any = null  // Singleton instance — safe to cache (Pinia stores are singletons)
let cachedViewingProviderStore: any = null
let toastRef: any = null
let routerRef: any = null
let piniaInstance: any = null

// CSRF token storage
let csrfToken: string | null = null
let csrfTokenPromise: Promise<string | null> | null = null

// Token refresh queue - prevents multiple simultaneous refresh attempts
// When multiple 401s fire at once, only one refresh runs and others wait for the result
let isRefreshingToken = false
let refreshSubscribers: Array<(token: string) => void> = []
let refreshFailSubscribers: Array<(error: any) => void> = []

function subscribeToTokenRefresh(): Promise<string> {
  return new Promise((resolve, reject) => {
    refreshSubscribers.push(resolve)
    refreshFailSubscribers.push(reject)
  })
}

function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach(cb => cb(token))
  refreshSubscribers = []
  refreshFailSubscribers = []
}

function onTokenRefreshFailed(error: any) {
  refreshFailSubscribers.forEach(cb => cb(error))
  refreshSubscribers = []
  refreshFailSubscribers = []
}

// Set Pinia instance for auth store access
export function setPiniaInstance(pinia: any) {
  piniaInstance = pinia
}

// Get auth store with proper Pinia context.
// Caches the singleton instance so useAuthStore() is never called from inside
// an axios interceptor (outside Vue component context), which can throw in some
// Pinia/Vite configurations. The Pinia instance is passed explicitly for
// out-of-component usage as documented at pinia.vuejs.org/core-concepts/outside-component-usage
function getAuthStore() {
  if (cachedAuthStore) return cachedAuthStore

  // Dev HMR recovery: module-level vars reset when api.ts hot-reloads,
  // but Pinia stores survive — re-acquire via the active Pinia instance.
  const activePinia = getActivePinia()
  if (activePinia && (activePinia as any)._s?.has('auth')) {
    cachedAuthStore = (activePinia as any)._s.get('auth')
    if (!cachedViewingProviderStore && (activePinia as any)._s?.has('viewingProvider')) {
      cachedViewingProviderStore = (activePinia as any)._s.get('viewingProvider')
    }
    return cachedAuthStore
  }

  if (!authStoreRef) return null
  try {
    const store = authStoreRef(piniaInstance)
    if (store) cachedAuthStore = store
    return store
  } catch (error) {
    console.warn('⚠️ Could not get auth store:', error)
    return null
  }
}

// Initialize references after stores are available.
// Pass useAuthStoreFn directly (from main.ts) to set authStoreRef synchronously and
// avoid a race condition where the dynamic import resolves after onMounted API calls fire.
export function initializeApiInterceptors(useAuthStoreFn?: () => any) {
  if (useAuthStoreFn) {
    authStoreRef = useAuthStoreFn
    // Prime the cache immediately — piniaInstance is already set via setPiniaInstance()
    // before this function is called (see main.ts). This ensures getAuthStore() never
    // needs to call the composable from inside an axios interceptor.
    try {
      cachedAuthStore = useAuthStoreFn(piniaInstance)
    } catch (e) {
      // Pinia not fully ready yet — cache will be populated on first request
    }
  } else {
    // Fallback: dynamic import (may not resolve before first protected request)
    import('@/stores/auth').then(({ useAuthStore }) => {
      authStoreRef = useAuthStore
      console.log('✅ Auth store reference initialized (dynamic fallback)')
    })
  }
  import('vue-toastification').then(({ useToast }) => {
    toastRef = useToast
  })
  import('@/router').then((router) => {
    routerRef = router.default
  })
  import('@/stores/viewingProvider').then(({ useViewingProviderStore }) => {
    try {
      cachedViewingProviderStore = useViewingProviderStore(piniaInstance)
    } catch {}
  })
  // Fetch initial CSRF token
  fetchCsrfToken()
}

// ─── Multi-region API resolution ────────────────────────────────────
// One FE bundle serves every regional variant. The API host is derived
// from window.location so the backend receives the correct Host header
// and can resolve the region per-request. Without this, a browser on
// pk-staging.zaveato.co.uk would call api-staging.zaveato.co.uk (baked
// into .env at build time), the backend would see Host=api-staging and
// resolve to GB, and every PK request would silently return GB data.
//
// SINGLE source of truth for the host→API map: `HOST_TO_API_HOST` in
// `config/hosts.ts`. This file used to hardcode the switch — deleted for
// SOLID (SRP: config/hosts owns the registry; api.ts owns request lifecycle;
// OCP: adding a new region = one line in config/hosts, this function needs
// no edit). Unknown hosts return null so callers fall through to the
// configured VITE_API_URL — never rewritten to a nonexistent api-* host.
function deriveApiHostForZaveato(browserHost: string): string | null {
  return HOST_TO_API_HOST.get(browserHost) ?? null
}

function resolveApiBaseUrl(): string {
  const configured = import.meta.env.VITE_API_URL || 'http://localhost:50000/api'
  if (typeof window === 'undefined') return configured

  // SRP note: this runs at HTTP-client init (before Vue setup context), so
  // `useCurrentHost` isn't reachable here. Classification is delegated to
  // the canonical `HOST_TO_API_HOST` map — this line only reads the input.
  const browserHost = window.location.hostname

  // Dev: browser on zaveato.local / zaveato.pk.local, backend on the same
  // hostname at port 8080. Swap the hostname (keep the port from .env).
  if (import.meta.env.DEV) {
    try {
      const cfg = new URL(configured)
      if (cfg.hostname === 'localhost' && browserHost !== 'localhost') {
        cfg.hostname = browserHost
        return cfg.toString().replace(/\/$/, '')
      }
    } catch {
      // configured wasn't a valid URL — leave it alone.
    }
    return configured
  }

  // Staging / prod: derive the API host from the browser location so one
  // build serves all regional variants. Unknown host falls through to the
  // .env value (which stays as a safety net).
  const derivedHost = deriveApiHostForZaveato(browserHost)
  if (!derivedHost) return configured
  try {
    const cfg = new URL(configured)
    cfg.hostname = derivedHost
    return cfg.toString().replace(/\/$/, '')
  } catch {
    return configured
  }
}

const API_BASE_URL = resolveApiBaseUrl()

// Fetch CSRF token from server
async function fetchCsrfToken(): Promise<string | null> {
  // If a fetch is already in progress, wait for it instead of starting another
  if (csrfTokenPromise) {
    return csrfTokenPromise
  }

  // Create and store the promise so concurrent calls can wait for it
  csrfTokenPromise = (async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/csrf-token`, {
        withCredentials: true
      })
      csrfToken = response.data.token
      console.log('🔒 CSRF token fetched successfully')
      return csrfToken
    } catch (error) {
      console.error('❌ Failed to fetch CSRF token:', error)
      return null
    } finally {
      // Clear the promise after completion so future calls can fetch a new token if needed
      csrfTokenPromise = null
    }
  })()

  return csrfTokenPromise
}

// Refresh CSRF token if needed
async function refreshCsrfTokenIfNeeded(): Promise<void> {
  // If a fetch is in progress, wait for it
  if (csrfTokenPromise) {
    await csrfTokenPromise
    return
  }

  // If no token, fetch one
  if (!csrfToken) {
    await fetchCsrfToken()
  }
}

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // Increased to 60 seconds
  headers: {
    'Content-Type': 'application/json',
  },
  // Always include credentials (cookies) with requests
  // Required for httpOnly refresh token cookie to work
  withCredentials: true,
  // Serialize arrays properly for .NET backend (e.g., categoryIds=id1&categoryIds=id2)
  paramsSerializer: {
    serialize: (params) => {
      const searchParams = new URLSearchParams()
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // For arrays, add each value with the same key (format .NET expects)
          value.forEach(v => searchParams.append(key, v))
        } else if (value !== undefined && value !== null) {
          searchParams.append(key, String(value))
        }
      })
      return searchParams.toString()
    }
  }
})

// Request interceptor
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Define all public endpoints that don't need authentication
    const publicEndpoints = [
      '/auth/login',
      '/auth/register',
      '/auth/refresh',
      '/auth/forgot-password',
      '/auth/reset-password',
      '/auth/verify-email',
      '/auth/check-email',
      '/auth/csrf-token', // CSRF token endpoint itself doesn't need token
      '/public/services',
      '/public/categories',
      '/public/providers',
      '/api/public/services',
      '/api/public/categories',
      '/api/public/providers',
      '/appointments/guest',
      '/appointments/recurring/guest', // Guest recurring bookings
      '/appointments/recurring/validate-availability', // Validate recurring availability
      '/appointments/services/',
      '/appointments/lookup/',
      '/appointments/public/', // Public appointment view via QR code
      '/payments/webhook',
      '/shop/checkout', // Shop checkout endpoints (public storefront)
      '/shop/storefront', // Shop storefront endpoints
      '/blog', // Public blog endpoints (no auth required)
    ]
    // Also check for public storefront shop browsing (but NOT /shop/shops/ which requires auth)
    // Must NOT match /shop/shops/ (provider management) — only /shops/ (public storefront)
    const isStorefrontShopEndpoint = config.url?.match(/^\/shops\/[^/]*\/?(products|categories|opening-hours)?$/) ||
                                      config.url?.match(/^\/shops\?/) ||
                                      config.url?.match(/^\/shops\/products/)
    const isPublicEndpoint = !config.url?.startsWith('/admin/') &&
                             (publicEndpoints.some(endpoint => config.url?.includes(endpoint)) ||
                             (isStorefrontShopEndpoint && !config.url?.startsWith('/shop/')))

    // SECURITY: Tokens are only stored in memory (auth store), NOT in
    // localStorage. This protects against XSS attacks.
    //
    // We SPLIT the auth handling into two decisions:
    //   1) Proactive refresh — only runs for non-public endpoints, so an
    //      anonymous visitor browsing the marketplace never fires refresh
    //      calls.
    //   2) Attaching the token — happens unconditionally when we have one.
    //      Some "public" endpoints (wholesale storefront, blog personalisation,
    //      shop reviews with own-comment editing) return DIFFERENT data when
    //      the caller is authenticated. Previously we skipped the token here,
    //      which meant a signed-in wholesale buyer got 404 on /shops/{code}
    //      because the BE couldn't identify them for the ApprovedBuyer gate.
    try {
      const authStore = getAuthStore()

      if (authStore) {
        // Proactive refresh only when we're going to hit an auth-required
        // endpoint — otherwise we might fire refresh calls for anon browsing.
        if (!isPublicEndpoint && authStore.accessToken && authStore.isTokenExpired && authStore.refreshToken) {
          if (isRefreshingToken) {
            const newToken = await subscribeToTokenRefresh()
            if (config.headers) {
              config.headers.Authorization = `Bearer ${newToken}`
            }
            return config
          }

          isRefreshingToken = true
          try {
            console.log('🔄 Token expired, proactively refreshing before request:', config.url)
            const success = await authStore.refreshTokens(true)
            if (success && authStore.accessToken) {
              onTokenRefreshed(authStore.accessToken)
            } else {
              onTokenRefreshFailed(new Error('Proactive refresh failed'))
            }
          } catch (e) {
            onTokenRefreshFailed(e)
          } finally {
            isRefreshingToken = false
          }
        }

        // Attach the token whenever we have one. The BE always ignores
        // Authorization when the endpoint is truly anonymous, so this is
        // safe for public endpoints and load-bearing for the gated ones.
        if (authStore.accessToken && config.headers) {
          config.headers.Authorization = `Bearer ${authStore.accessToken}`
        }
      } else if (!isPublicEndpoint) {
        console.warn('⚠️ Auth store not available for request:', config.url)
      }
    } catch (error) {
      console.warn('⚠️ Could not access auth store for token:', error)
    }

    // Inject ?providerId for admin-viewing-as-provider (backend reads this to scope to correct provider)
    // Pinia unwraps computed refs on store access, so getViewingProviderId is already the raw value
    const viewingProviderId = cachedViewingProviderStore?.getViewingProviderId
    if (viewingProviderId) {
      config.params = { ...config.params, providerId: viewingProviderId }
    }

    // Add CSRF token for state-changing requests (POST, PUT, PATCH, DELETE)
    const isStateChangingRequest = ['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase() || '')
    if (isStateChangingRequest && config.headers) {
      // Ensure we have a CSRF token
      await refreshCsrfTokenIfNeeded()

      if (csrfToken) {
        config.headers['X-XSRF-TOKEN'] = csrfToken
        console.log('🔒 CSRF token added to request')
      } else {
        console.warn('⚠️ No CSRF token available for state-changing request')
      }
    }

    // Add request timestamp for debugging
    config.metadata = { startTime: Date.now() }

    console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`,
                isPublicEndpoint ? '(Public Endpoint)' : '',
                config.headers?.Authorization ? '(With Auth)' : '(No Auth)',
                config.headers?.['X-XSRF-TOKEN'] ? '(With CSRF)' : '')

    return config
  },
  (error: AxiosError) => {
    console.error('❌ Request Error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Calculate request duration
    const duration = Date.now() - (response.config.metadata?.startTime || 0)
    console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url} (${duration}ms)`)
    // Note: Response data not logged for security (may contain PII, tokens, payment data)

    return response
  },
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config

    // Calculate request duration
    const duration = Date.now() - (originalRequest?.metadata?.startTime || 0)
    console.error(`❌ API Error: ${originalRequest?.method?.toUpperCase() || 'UNKNOWN'} ${originalRequest?.url || 'UNKNOWN_URL'} (${duration}ms)`, {
      status: error.response?.status,
      statusText: error.response?.statusText,
      message: error.message
      // Note: error.response?.data not logged for security (may contain PII in validation errors)
    })

    // Handle 401 errors (unauthorized) - but skip all public endpoints to avoid loops
    const publicEndpoints = [
      '/auth/login',
      '/auth/register',
      '/auth/refresh',
      '/auth/forgot-password',
      '/auth/reset-password',
      '/auth/verify-email',
      '/auth/check-email',
      '/auth/csrf-token',
      '/public/services',
      '/public/categories',
      '/public/providers',
      '/api/public/services',
      '/api/public/categories',
      '/api/public/providers',
      '/appointments/guest',
      '/appointments/recurring/guest', // Guest recurring bookings
      '/appointments/recurring/validate-availability', // Validate recurring availability
      '/appointments/services/',
      '/appointments/lookup/',
      '/appointments/public/', // Public appointment view via QR code
      '/payments/webhook',
      '/shop/checkout', // Shop checkout endpoints (public storefront)
      '/shop/storefront', // Shop storefront endpoints
      '/blog', // Public blog endpoints (no auth required)
    ]
    // Also check for public storefront shop browsing (but NOT /shop/shops/ which requires auth)
    const isStorefrontShopEndpoint = originalRequest?.url?.match(/^\/shops\/[^/]*\/?(products|categories|opening-hours)?/) ||
                                      originalRequest?.url?.match(/^\/shops\?/) ||
                                      originalRequest?.url?.match(/^\/shops\/products/)
    const isPublicEndpoint = !originalRequest?.url?.startsWith('/admin/') &&
                             (publicEndpoints.some(endpoint => originalRequest?.url?.includes(endpoint)) || isStorefrontShopEndpoint)

    // Handle 400 errors that might be CSRF token validation failures
    if (error.response?.status === 400 && originalRequest && !originalRequest._csrfRetry) {
      const errorMessage = error.response?.data?.message?.toLowerCase() || ''
      // Only retry for actual CSRF/antiforgery errors, not other "token" errors like password reset tokens
      const isCsrfError = errorMessage.includes('antiforgery') ||
                          errorMessage.includes('csrf') ||
                          errorMessage.includes('xsrf') ||
                          (errorMessage.includes('token') && errorMessage.includes('validation'))

      if (isCsrfError) {
        originalRequest._csrfRetry = true
        console.log('🔄 CSRF validation failed, fetching new token and retrying...')

        // Fetch new CSRF token
        await fetchCsrfToken()

        if (csrfToken && originalRequest.headers) {
          originalRequest.headers['X-XSRF-TOKEN'] = csrfToken
          console.log('✅ Retrying request with new CSRF token')
          return api(originalRequest)
        }
      }
    }

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !isPublicEndpoint && authStoreRef) {
      originalRequest._retry = true

      // If a refresh is already in progress, queue this request to retry after refresh completes
      if (isRefreshingToken) {
        console.log('🔄 Token refresh in progress, queuing request:', originalRequest.url)
        try {
          const newToken = await subscribeToTokenRefresh()
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`
          }
          return api(originalRequest)
        } catch {
          return Promise.reject(new SessionExpiredError())
        }
      }

      // This request is the first to trigger refresh — it owns the refresh process
      isRefreshingToken = true

      try {
        console.log('🔄 401 detected, refreshing token for:', originalRequest.url)

        const authStore = getAuthStore()

        if (!authStore || !authStore.refreshToken) {
          console.warn('⚠️ No refresh token available, session expired')
          throw new Error('No refresh token available')
        }

        const refreshSuccess = await authStore.refreshTokens(true)

        if (refreshSuccess && authStore.accessToken) {
          // Notify all queued requests with the new token
          onTokenRefreshed(authStore.accessToken)

          // Retry the original request
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${authStore.accessToken}`
          }

          console.log('✅ Token refreshed, retrying:', originalRequest.url)
          return api(originalRequest)
        } else {
          throw new Error('Token refresh failed')
        }
      } catch (refreshError: any) {
        console.error('❌ Token refresh failed:', refreshError)

        // Notify all queued requests that refresh failed
        onTokenRefreshFailed(refreshError)

        const authStore = getAuthStore()
        if (authStore && toastRef) {
          const toast = toastRef()
          const currentPath = routerRef?.currentRoute?.value?.fullPath
          authStore.logout(true, currentPath)
          toast.info('Your session has expired. Please login to continue.')
        } else if (routerRef) {
          // Strip auth-route paths from the redirect target — sending the user
          // back to /auth/* after re-login is a circular flow (e.g., end up on
          // /auth/register after logging in). Use plain /login in those cases.
          const currentPath = routerRef.currentRoute?.value?.fullPath
          const safeRedirect = currentPath && !currentPath.startsWith('/auth/') ? currentPath : null
          const loginPath = safeRedirect ? `/login?redirect=${encodeURIComponent(safeRedirect)}` : '/login'
          routerRef.push(loginPath)
        }

        return Promise.reject(new SessionExpiredError())
      } finally {
        isRefreshingToken = false
      }
    }

    // For 401 errors that were already retried, treat as session expired
    if (error.response?.status === 401 && originalRequest?._retry && !isPublicEndpoint) {
      console.warn('⚠️ Request failed after token refresh, session expired')
      return Promise.reject(new SessionExpiredError())
    }

    // Handle other error status codes
    const errorMessage = getErrorMessage(error)

    // Don't show toast for public endpoints to avoid spam
    // Also don't show toast for session expired errors (already handled above)
    if (!isPublicEndpoint && toastRef && error.response?.status !== 401) {
      const toast = toastRef()
      toast.error(errorMessage)
    }

    // Report 5xx server errors to the admin audit log. Fire-and-forget; never
    // blocks the caller and never surfaces a UI error itself. Skipped for
    // reporter's own endpoint (would infinite-loop) and for public endpoints
    // (a 500 on a public marketing surface is still worth logging — kept in).
    const status = error.response?.status ?? 0
    if (status >= 500 && !isReporterEndpoint(originalRequest?.url)) {
      reportClientError({
        message: `${originalRequest?.method?.toUpperCase() ?? 'REQ'} ${originalRequest?.url ?? '?'} → ${status}: ${errorMessage}`,
        stack: error.stack,
        endpoint: originalRequest?.url,
        statusCode: status,
        context: 'axios:5xx',
      })
    }

    return Promise.reject(error)
  }
)

// Helper function to extract error message
function getErrorMessage(error: AxiosError<ApiError>): string {
  const responseData = error.response?.data as any

  // Prefer userMessage — the middleware sanitises it (never contains driver
  // internals, exception types, stack traces). `message` is the raw technical
  // string which is useful in logs but historically leaked EF Core /
  // MySqlException text to the UI (ZAV-024). Fall back to `message` only if
  // no userMessage was set (e.g. non-middleware error paths).
  if (responseData?.userMessage) {
    return responseData.userMessage
  }
  if (responseData?.message) {
    return responseData.message
  }

  // Check for FluentValidation errors format (errors object with field arrays)
  if (responseData?.errors && typeof responseData.errors === 'object') {
    const errorMessages: string[] = []
    for (const field of Object.keys(responseData.errors)) {
      const fieldErrors = responseData.errors[field]
      if (Array.isArray(fieldErrors)) {
        errorMessages.push(...fieldErrors)
      }
    }
    if (errorMessages.length > 0) {
      return errorMessages.join('. ')
    }
  }

  // Check for title/detail format (ProblemDetails)
  if (responseData?.title || responseData?.detail) {
    return responseData.detail || responseData.title
  }

  switch (error.response?.status) {
    case 400:
      return 'Bad request. Please check your input.'
    case 401:
      return 'Unauthorized. Please login again.'
    case 403:
      return 'Access denied. You don\'t have permission to perform this action.'
    case 404:
      return 'Resource not found.'
    case 409:
      return 'Conflict. The resource already exists.'
    case 422:
      return 'Validation error. Please check your input.'
    case 500:
      return 'Internal server error. Please try again later.'
    case 502:
      return 'Bad gateway. The server is temporarily unavailable.'
    case 503:
      return 'Service unavailable. Please try again later.'
    default:
      return error.message || 'An unexpected error occurred.'
  }
}

/**
 * Extract per-field validation errors from an API error response.
 *
 * Backend returns errors in RFC 7807 ProblemDetails shape:
 *   { errors: { "FieldName": ["msg1", "msg2"], ... } }
 * (FluentValidation, DataAnnotations, and our GlobalExceptionHandlerMiddleware
 * all produce this shape.)
 *
 * Returns a normalised { fieldNameLowerCamel: string[] } map for binding to
 * form field state, or null if there are no field-level errors on the response.
 *
 * Historically forms only got the joined message from getErrorMessage() and
 * showed a generic toast — you couldn't tell which field failed (bugs
 * ZAV-007, ZAV-012, ZAV-013). Call this in your catch block to highlight the
 * specific field(s) that failed.
 */
export function extractFieldErrors(error: any): Record<string, string[]> | null {
  const responseData = error?.response?.data
  if (!responseData?.errors || typeof responseData.errors !== 'object') {
    return null
  }

  const normalised: Record<string, string[]> = {}
  for (const [rawField, msgs] of Object.entries(responseData.errors as Record<string, unknown>)) {
    if (!Array.isArray(msgs) || msgs.length === 0) continue
    // Normalise field name: server returns PascalCase (C# property names) or
    // sometimes dotted paths (Address.PostCode). Convert to lowerCamelCase for
    // matching form v-model paths, and keep the original for debug.
    const key = rawField.charAt(0).toLowerCase() + rawField.slice(1)
    normalised[key] = (msgs as string[]).map((m) => String(m))
  }
  return Object.keys(normalised).length > 0 ? normalised : null
}

// Generic API methods
export const apiClient = {
  // GET request
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await api.get<T>(url, config)
    console.log("API Endpoint URL: " + url);
    return response.data
  },
  
  // POST request
  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await api.post<T>(url, data, config)
    return response.data
  },
  
  // PUT request
  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await api.put<T>(url, data, config)
      return response.data
    } catch (error) {
      console.error('API PUT Error:', { url })
      throw error
    }
  },
  
  // PATCH request
  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await api.patch<T>(url, data, config)
    return response.data
  },
  
  // DELETE request
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await api.delete<T>(url, config)
    return response.data
  },
  
  // File upload
  async upload<T = any>(url: string, file: File, config?: AxiosRequestConfig): Promise<T> {
    const formData = new FormData()
    formData.append('file', file)
    
    const response = await api.post<T>(url, formData, {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': 'multipart/form-data',
      },
    })
    
    return response.data
  },
  
  // Download file
  async download(url: string, filename?: string, config?: AxiosRequestConfig): Promise<void> {
    const response = await api.get(url, {
      ...config,
      responseType: 'blob',
    })

    const blob = new Blob([response.data])
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename || 'download'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  },

  // Fetch a binary response as a Blob (useful for PDFs etc. that we need to
  // hand to the native share sheet instead of just downloading).
  async getBlob(url: string, config?: AxiosRequestConfig): Promise<Blob> {
    const response = await api.get(url, {
      ...config,
      responseType: 'blob',
    })
    return new Blob([response.data], {
      type: response.headers['content-type'] || 'application/octet-stream',
    })
  },

  // POST and receive a Blob — used for PDF-generation endpoints that need a
  // body (e.g. selected invoice IDs) but still return binary.
  async postBlob(url: string, data?: any, config?: AxiosRequestConfig): Promise<Blob> {
    const response = await api.post(url, data, {
      ...config,
      responseType: 'blob',
    })
    return new Blob([response.data], {
      type: response.headers['content-type'] || 'application/octet-stream',
    })
  },

  // Generic request method for advanced usage (returns full response)
  async request<T = any>(config: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return api.request<T>(config)
  }
}

// Export the axios instance for advanced usage
export default api

// Extend AxiosRequestConfig to include metadata
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    _retry?: boolean
    _retryCount?: number
    _csrfRetry?: boolean
    metadata?: {
      startTime: number
    }
  }
}